<template>
  <base-modal :is-form-visible="isVisible" @close="close">
    <template #header>
      <h2 class="text-lg">{{ editInvoice ? `Edit Invoice #${editInvoice.invoice_number || editInvoice.id} (regenerates PDF)` : 'Create Invoice' }}</h2>
    </template>
    <template #body>
      <div class="max-h-[85vh] overflow-auto p-2 space-y-3 min-w-[60rem]">
        <div v-if="previewUrl" class="space-y-2">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-600">Preview (not yet saved)</div>
            <base-button @click="closePreview">← Back to edit</base-button>
          </div>
          <iframe :src="previewUrl" class="w-full h-[70vh] border rounded"></iframe>
        </div>
        <div v-show="!previewUrl" class="space-y-3">
        <div class="flex items-center space-x-2">
          <label class="font-semibold w-32">Engagement</label>
          <select v-model.number="form.engagement_id" class="border rounded px-2 py-1 flex-1" :disabled="pickerLoading">
            <option :value="null">{{ pickerLoading ? 'Loading engagements…' : '— pick an engagement —' }}</option>
            <option v-for="eng in engagementOptions" :key="eng.id" :value="eng.id">
              {{ eng.client?.client_name || '—' }} - {{ eng.title || eng.invoice_label || '—' }} ({{ eng.id }}){{ eng.is_paused ? ' — paused' : '' }}
            </option>
          </select>
        </div>

        <div v-if="form.engagement_id" class="flex flex-wrap items-center gap-3 pl-32">
          <div class="flex items-center space-x-2">
            <label class="text-sm">Copy from previous invoice:</label>
            <select :value="''" @change="copyFromInvoice($event.target.value)" class="border rounded px-2 py-1 text-sm" :disabled="busy || priorInvoices.length === 0">
              <option value="">— select an invoice —</option>
              <option v-for="inv in priorInvoices" :key="inv.id" :value="inv.id">
                #{{ inv.invoice_number || inv.id }} · {{ inv.invoice_date || 'no date' }}{{ inv.status === 'Draft' ? ' · DRAFT' : '' }}
              </option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-x-6 gap-y-2 pt-2">
          <div class="flex items-center space-x-2">
            <label class="font-semibold w-32 relative">
              <span class="cursor-help" @mouseenter="showTop10 = true" @mouseleave="showTop10 = false">
                Invoice #
                <span class="text-xs text-gray-400 align-super">ⓘ</span>
              </span>
              <div v-show="showTop10 && topInvoiceNumbers.length"
                   class="absolute left-0 top-full mt-1 z-50 bg-white border rounded shadow-lg p-2 text-xs w-[28rem]">
                <div class="font-semibold mb-1">Top 10 invoice numbers in the system</div>
                <table class="w-full">
                  <thead class="text-gray-600">
                    <tr><th class="text-left pr-3">#</th><th class="text-left pr-3">Client</th><th class="text-left pr-3">Date</th><th class="text-left">Status</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="inv in topInvoiceNumbers" :key="inv.id" class="font-mono">
                      <td class="pr-3">{{ inv.invoice_number ?? '—' }}</td>
                      <td class="pr-3 font-sans">{{ inv.engagement?.client?.client_name || '—' }}</td>
                      <td class="pr-3">{{ inv.invoice_date || '—' }}</td>
                      <td class="font-sans">{{ inv.status || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </label>
            <input v-model.number="form.invoice_number" type="number" class="border rounded px-2 py-1 flex-1" />
          </div>
          <div class="flex items-center space-x-2">
            <label class="font-semibold w-32">Is Retainer</label>
            <input v-model="form.is_retainer_fee" type="checkbox" />
            <span class="text-xs text-gray-500">
              defaults from the engagement type — uncheck for off-cycle / expense-reimbursement invoices
            </span>
          </div>
          <div class="flex items-center space-x-2">
            <label class="font-semibold w-32">Invoice Date</label>
            <input v-model="form.invoice_date" type="date" class="border rounded px-2 py-1 flex-1" />
          </div>
          <div class="flex items-center space-x-2">
            <label class="font-semibold w-32">Due Date</label>
            <input v-model="form.due_date" type="date" class="border rounded px-2 py-1 flex-1" />
            <span v-if="daysBetweenDates != null" class="text-xs whitespace-nowrap" :class="daysBetweenDates === 21 ? 'text-gray-500' : 'text-amber-700'">
              {{ daysBetweenDates }}d after invoice
            </span>
          </div>
        </div>

        <div class="space-y-1 pt-2">
          <div class="font-semibold">Bill to <span class="text-xs text-gray-500 font-normal">(defaults from engagement; edit to override on this invoice)</span></div>
          <div class="grid grid-cols-2 gap-x-6 gap-y-2">
            <div class="flex items-center space-x-2">
              <label class="font-semibold w-32">Company</label>
              <input v-model="form.bill_to_company_name" class="border rounded px-2 py-1 flex-1" />
            </div>
            <div class="flex items-center space-x-2">
              <label class="font-semibold w-32">Person</label>
              <input v-model="form.bill_to_person" class="border rounded px-2 py-1 flex-1" />
            </div>
            <div class="flex items-start space-x-2 col-span-2">
              <label class="font-semibold w-32 mt-1">Address</label>
              <textarea v-model="form.bill_to_address" rows="3" class="border rounded px-2 py-1 flex-1" placeholder="Street&#10;City, State ZIP&#10;Country"></textarea>
            </div>
            <div class="flex items-center space-x-2 col-span-2">
              <label class="font-semibold w-32">Email</label>
              <input v-model="form.bill_to_email_address" type="email" class="border rounded px-2 py-1 flex-1" />
            </div>
          </div>
        </div>

        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <label class="font-semibold">Line items</label>
            <base-button @click="addItem">Add item</base-button>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left p-1 w-40">Item</th>
                <th class="text-left p-1">Description</th>
                <th class="text-right p-1 w-20">Qty</th>
                <th class="text-right p-1 w-28">Price</th>
                <th class="text-right p-1 w-28">Amount</th>
                <th class="w-8"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in form.items" :key="idx" class="border-b">
                <td><input v-model="item.product_name" class="border rounded px-1 py-0.5 w-full" /></td>
                <td><input v-model="item.description" class="border rounded px-1 py-0.5 w-full" /></td>
                <td><input v-model.number="item.quantity" type="number" step="0.01" class="border rounded px-1 py-0.5 w-full text-right" /></td>
                <td><input v-model.number="item.price" type="number" step="0.01" class="border rounded px-1 py-0.5 w-full text-right" /></td>
                <td class="text-right pr-1">{{ formatMoney(lineTotal(item)) }}</td>
                <td><button @click="removeItem(idx)" class="text-red-600">✕</button></td>
              </tr>
              <tr v-if="form.items.length === 0">
                <td colspan="6" class="text-center text-gray-500 py-2">No items yet — click Add item.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="text-right font-semibold p-1">Total</td>
                <td class="text-right font-semibold p-1">{{ formatMoney(invoiceTotal) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="space-y-1 pt-2">
          <div class="flex items-center justify-between">
            <label class="font-semibold">Notes / Terms</label>
            <base-button @click="insertStandardPaymentInfo">Insert standard payment info</base-button>
          </div>
          <textarea v-model="form.notes" class="border rounded px-2 py-1 w-full font-mono text-xs" rows="10" placeholder="Paste ACH / wire instructions, payment terms, etc. — appears in the lower-left of the PDF."></textarea>
        </div>

        </div>

        <div v-if="recentInvoices.length" class="space-y-1 pt-3 border-t">
          <div class="font-semibold">Recent invoices for this engagement (last {{ recentInvoices.length }})</div>
          <div class="overflow-auto max-h-[40vh]">
            <SimpleInvoiceTable :invoices="recentInvoices" />
          </div>
        </div>

        <div v-if="errorMessage" class="text-red-600 text-sm">{{ errorMessage }}</div>

        <div class="flex justify-end space-x-2 pt-3 border-t">
          <base-button @click="close" :disabled="busy">Cancel</base-button>
          <base-button @click="preview" :disabled="!form.engagement_id || busy">Preview PDF</base-button>
          <base-button @click="save" :disabled="!form.engagement_id || busy">
            {{ busy ? 'Saving…' : (invoiceId ? 'Save & regenerate PDF' : 'Save & generate PDF') }}
          </base-button>
        </div>
      </div>

    </template>
  </base-modal>
  <base-modal :is-form-visible="showPausedWarning" @close="cancelPausedSelection">
    <template #header>
      <h2 class="text-lg">Engagement is on hold</h2>
    </template>
    <template #body>
      <div class="p-2 min-w-[28rem] max-w-[36rem]">
        <p class="text-sm mb-2">
          <span class="font-semibold">
            {{ pendingPausedEngagement?.client?.client_name || '—' }} —
            {{ pendingPausedEngagement?.title || pendingPausedEngagement?.invoice_label || `Engagement #${pendingPausedEngagement?.id}` }}
          </span>
          is currently paused.
        </p>
        <p v-if="pendingPausedEngagement?.current_pause_reason" class="text-sm text-gray-700 italic mb-3">
          Reason: {{ pendingPausedEngagement.current_pause_reason }}
        </p>
        <p class="text-sm text-gray-700 mb-4">
          Retainer invoices for this engagement are paused. It's still fine to invoice for expense reimbursements
          (or other non-retainer items) while the retainer is on hold. Do you want to continue?
        </p>
        <div class="flex justify-end space-x-2">
          <base-button-negative @click.prevent="cancelPausedSelection">Cancel</base-button-negative>
          <base-button-affirmative @click.prevent="confirmPausedSelection">Yes, continue</base-button-affirmative>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
export default { name: 'invoice-draft-modal' }
</script>

<script setup>
import { ref, reactive, computed, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import SimpleInvoiceTable from './SimpleInvoiceTable.vue'
import { createInvoiceDraft, saveInvoice, previewInvoicePdf, getInvoices } from '@/services/Invoice.js'
import { getEngagements } from '@/services/Engagement.js'

const props = defineProps({
  isVisible: Boolean,
  engagements: { type: Array, default: () => [] },
  // When set, the modal opens in edit mode for an EXISTING invoice (dev use:
  // correcting an already-sent invoice). Saving re-renders its PDF, keeps the
  // same invoice number, and archives the new PDF version.
  editInvoice: { type: Object, default: null },
})
const emit = defineEmits(['close', 'saved'])

const store = useStore()
const $toast = useToast()
const token = computed(() => store.state.auth.token)

const busy = ref(false)
const errorMessage = ref('')
const invoiceId = ref(null)
const previewUrl = ref('')
const topInvoiceNumbers = ref([])
const showTop10 = ref(false)
const showPausedWarning = ref(false)
const pendingPausedEngagement = ref(null)
const confirmedPausedEngagementId = ref(null)
const pickerEngagements = ref([])
const pickerLoading = ref(false)

const form = reactive(blankForm())

function blankForm() {
  return {
    engagement_id: null,
    invoice_number: null,
    invoice_date: null,
    due_date: null,
    is_retainer_fee: false,
    bill_to_company_name: '',
    bill_to_person: '',
    bill_to_address: '',
    bill_to_email_address: '',
    notes: '',
    items: [],
  }
}

const engagementSource = computed(() => pickerEngagements.value)

const engagementOptions = computed(() => {
  return [...engagementSource.value].sort((a, b) => {
    const an = a.client?.client_name || ''
    const bn = b.client?.client_name || ''
    return an.localeCompare(bn)
  })
})

const selectedEngagement = computed(() =>
  engagementSource.value.find((e) => e.id === form.engagement_id) || null
)

const priorInvoices = computed(() => {
  const list = selectedEngagement.value?.invoices || []
  return [...list].sort((a, b) => {
    const ad = a.invoice_date || ''
    const bd = b.invoice_date || ''
    if (ad === bd) return (b.id || 0) - (a.id || 0)
    return ad < bd ? 1 : -1
  })
})

const recentInvoices = computed(() => priorInvoices.value.slice(0, 10))

const daysBetweenDates = computed(() => {
  if (!form.invoice_date || !form.due_date) return null
  const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(form.invoice_date)
  const m2 = /^(\d{4})-(\d{2})-(\d{2})/.exec(form.due_date)
  if (!m1 || !m2) return null
  const d1 = new Date(Number(m1[1]), Number(m1[2]) - 1, Number(m1[3]))
  const d2 = new Date(Number(m2[1]), Number(m2[2]) - 1, Number(m2[3]))
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24))
})

watch(() => props.isVisible, (v) => {
  if (v) {
    resetForm()
    loadTopInvoiceNumbers()
    loadPickerEngagements()
    if (props.editInvoice && props.editInvoice.id) {
      // Edit mode: load the existing invoice's data, keep its number/dates.
      // The save() path then re-saves this invoice id, regenerating the PDF.
      invoiceId.value = props.editInvoice.id
      form.engagement_id = props.editInvoice.engagement ? props.editInvoice.engagement.id : null
      applyInvoiceData(props.editInvoice, { resetNumber: false, resetDates: false })
    }
  }
})

async function loadPickerEngagements() {
  pickerLoading.value = true
  pickerEngagements.value = []
  try {
    // status=active intentionally NOT applied: the backend treats `active` as
    // "is_paused=False AND not ended", which excludes paused engagements.
    // Drop the status filter and exclude only truly-ended engagements on the
    // client side. retainer_on is also unfiltered so expense-reimbursement
    // invoices can target non-retainer engagements.
    const result = await getEngagements(token.value, '?should_invoice=true&page_size=500')
    const rows = result?.results || []
    const todayStr = new Date().toISOString().slice(0, 10)
    pickerEngagements.value = rows.filter((e) => !e.end_date || e.end_date >= todayStr)
  } catch (err) {
    pickerEngagements.value = []
  } finally {
    pickerLoading.value = false
  }
}

// When engagement changes, default the Bill to section from the engagement.
// If the engagement itself has no bill_to_* (e.g., a new engagement that
// hasn't been backfilled), fall back to the most recent invoice that
// carries those fields. Only fields the user hasn't already typed into
// are overwritten.
watch(() => form.engagement_id, () => {
  const eng = selectedEngagement.value
  if (!eng) return
  if (eng.is_paused && confirmedPausedEngagementId.value !== eng.id) {
    pendingPausedEngagement.value = eng
    showPausedWarning.value = true
    return
  }
  // Default the retainer flag from the engagement's compensation type. This
  // drives the Engagements "Days Until Next Calc. Invoice Day" calc, which only
  // counts is_retainer_fee invoices. Only default for a fresh invoice — in edit
  // mode the existing invoice's value (set by applyInvoiceData) is authoritative
  // and must not be clobbered. The user can still override via the checkbox
  // (e.g. uncheck for an off-cycle / expense-reimbursement invoice).
  const isEdit = !!(props.editInvoice && props.editInvoice.id)
  if (!isEdit) form.is_retainer_fee = eng.compensation_type === 'retainer'
  const lastInvWithBillTo = (eng.invoices || []).find((inv) =>
    inv.bill_to_company_name || inv.bill_to_person || inv.bill_to_address || inv.bill_to_email_address
  )
  const pick = (engVal, invVal) => engVal || invVal || ''
  if (!form.bill_to_company_name) form.bill_to_company_name = pick(eng.bill_to_company_name, lastInvWithBillTo?.bill_to_company_name)
  if (!form.bill_to_person) form.bill_to_person = pick(eng.bill_to_person, lastInvWithBillTo?.bill_to_person)
  if (!form.bill_to_address) form.bill_to_address = pick(eng.bill_to_address, lastInvWithBillTo?.bill_to_address)
  if (!form.bill_to_email_address) form.bill_to_email_address = pick(eng.bill_to_email_address, lastInvWithBillTo?.bill_to_email_address)
})

function confirmPausedSelection() {
  if (pendingPausedEngagement.value) {
    confirmedPausedEngagementId.value = pendingPausedEngagement.value.id
  }
  showPausedWarning.value = false
  pendingPausedEngagement.value = null
  // Re-trigger the engagement_id watcher so bill_to defaults populate now that
  // the user has confirmed. Toggling through null preserves any fields the
  // user already typed (the watcher only fills empty bill_to_*).
  const id = confirmedPausedEngagementId.value
  if (id) {
    form.engagement_id = null
    form.engagement_id = id
  }
}

function cancelPausedSelection() {
  showPausedWarning.value = false
  pendingPausedEngagement.value = null
  form.engagement_id = null
}

async function loadTopInvoiceNumbers() {
  try {
    const result = await getInvoices(token.value, '?order_by=-invoice_number&page_size=10')
    topInvoiceNumbers.value = result?.results || []
  } catch (err) {
    topInvoiceNumbers.value = []
  }
}

function resetForm() {
  Object.assign(form, blankForm())
  invoiceId.value = null
  errorMessage.value = ''
  showPausedWarning.value = false
  pendingPausedEngagement.value = null
  confirmedPausedEngagementId.value = null
  closePreview()
}

function closePreview() {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value)
    previewUrl.value = ''
  }
}

function close() {
  closePreview()
  emit('close')
}

function addItem() {
  form.items.push({ product_name: '', description: '', quantity: 1, price: 0 })
}

function removeItem(idx) {
  form.items.splice(idx, 1)
}

function lineTotal(item) {
  const q = Number(item.quantity) || 0
  const p = Number(item.price) || 0
  return q * p
}

const invoiceTotal = computed(() =>
  form.items.reduce((acc, item) => acc + lineTotal(item), 0)
)

function formatMoney(value) {
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
  return formatter.format(value || 0)
}

const STANDARD_PAYMENT_INFO = `Please submit payment by ACH:
Bank Name: JPMorgan Chase Bank, N.A.
ABA ACH Routing No: 111000614
Bank Account No: 785188225

Wire Transfers
For domestic, please use routing number 021000021
For international, please use Swift Code: CHASUS33XXX
JPMorgan Chase Bank, N.A.
270 Park Avenue, New York, NY 10017
Bank Account No: 785188225`

function insertStandardPaymentInfo() {
  if (form.notes && form.notes.trim()) {
    form.notes = form.notes.trimEnd() + '\n\n' + STANDARD_PAYMENT_INFO
  } else {
    form.notes = STANDARD_PAYMENT_INFO
  }
}

function copyFromInvoice(invoiceId) {
  if (!invoiceId) return
  const id = Number(invoiceId)
  const src = priorInvoices.value.find((i) => i.id === id)
  if (!src) return
  applyInvoiceData(src, { resetNumber: true, resetDates: true })
}

function applyInvoiceData(invoice, opts = {}) {
  form.invoice_number = opts.resetNumber ? null : invoice.invoice_number
  form.invoice_date = opts.resetDates ? null : invoice.invoice_date
  form.due_date = opts.resetDates ? null : invoice.due_date
  form.is_retainer_fee = !!invoice.is_retainer_fee
  // Only overwrite bill_to_* when the source has something meaningful —
  // a previous invoice with blank bill_to fields shouldn't wipe the form.
  const meaningful = (v) => typeof v === 'string' && v.trim().length > 0
  if (meaningful(invoice.bill_to_company_name)) form.bill_to_company_name = invoice.bill_to_company_name
  if (meaningful(invoice.bill_to_person)) form.bill_to_person = invoice.bill_to_person
  if (meaningful(invoice.bill_to_address)) form.bill_to_address = invoice.bill_to_address
  if (meaningful(invoice.bill_to_email_address)) form.bill_to_email_address = invoice.bill_to_email_address
  form.notes = invoice.notes || ''
  form.items = (invoice.items || []).map((it) => ({
    product_name: it.product_name || '',
    description: it.description || '',
    quantity: Number(it.quantity) || 0,
    price: Number(it.price) || 0,
  }))
}

async function preview() {
  if (!form.engagement_id) return
  busy.value = true
  errorMessage.value = ''
  try {
    const blob = await previewInvoicePdf({ ...form }, token.value)
    closePreview()
    previewUrl.value = URL.createObjectURL(blob)
  } catch (err) {
    let msg = 'Preview failed.'
    const data = err?.response?.data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        try {
          const json = JSON.parse(text)
          msg = json?.detail || JSON.stringify(json).slice(0, 400)
        } catch {
          msg = text.slice(0, 400)
        }
      } catch (_) { /* keep default */ }
    } else if (data?.detail) {
      msg = data.detail
    }
    errorMessage.value = `Preview failed: ${msg}`
    console.error('Preview failed', err)
  } finally {
    busy.value = false
  }
}

async function save() {
  if (!form.engagement_id) return
  busy.value = true
  errorMessage.value = ''
  try {
    const payload = { ...form }
    let invoice
    if (!invoiceId.value) {
      const created = await createInvoiceDraft(payload, token.value)
      invoiceId.value = created.id
      invoice = await saveInvoice(created.id, payload, token.value)
    } else {
      invoice = await saveInvoice(invoiceId.value, payload, token.value)
    }
    $toast.success(`Saved invoice #${invoice.invoice_number || invoice.id}`)
    emit('saved', invoice)
    close()
  } catch (err) {
    const detail = err?.response?.data
    errorMessage.value = typeof detail === 'string'
      ? detail
      : (detail?.invoice_number?.[0] || detail?.detail || 'Save failed.')
  } finally {
    busy.value = false
  }
}
</script>